import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useIsMobile } from '../hooks/useIsMobile'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import type { Todo } from '../api/types'
import { useUpdateTodo } from '../api/hooks'
import { useDebouncedCallback } from '../lib/useDebouncedCallback'
import { useNoteDrawerStore } from '../state/noteDrawer'
import { MarkdownEditor } from './MarkdownEditor'

const SAVE_DEBOUNCE_MS = 700

function findTodoInCache(qc: QueryClient, todoId: string): Todo | null {
  const queries = qc.getQueriesData<Todo[]>({ queryKey: ['todos'] })
  for (const [, data] of queries) {
    const found = data?.find((t) => t.id === todoId)
    if (found) return found
  }
  return null
}

export function NoteDrawer() {
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  const todoId = useNoteDrawerStore((s) => s.todoId)
  const closeStore = useNoteDrawerStore((s) => s.close)
  const updateTodo = useUpdateTodo()

  const todo = todoId ? findTodoInCache(queryClient, todoId) : null

  const [draft, setDraft] = useState('')

  const debouncedSave = useDebouncedCallback(
    (payload: { id: string; note: string | null }) => {
      updateTodo.mutate({ id: payload.id, note: payload.note })
    },
    SAVE_DEBOUNCE_MS
  )

  useLayoutEffect(() => {
    debouncedSave.flush()
    if (!todoId) {
      setDraft('')
      return
    }
    const found = findTodoInCache(queryClient, todoId)
    setDraft(found?.note ?? '')
  }, [todoId, debouncedSave, queryClient])

  const handleClose = useCallback(() => {
    debouncedSave.flush()
    closeStore()
  }, [debouncedSave, closeStore])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && todoId) handleClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [todoId, handleClose])

  const handleDraftChange = useCallback(
    (next: string) => {
      setDraft(next)
      if (!todoId) return
      /** Empty string clears the note (server maps "" → NULL via NULLIF). Sending JSON
       * `null` here would be indistinguishable from "field omitted" once decoded into
       * Go's *string, so the backend would keep the old value. See queries/todos.sql. */
      debouncedSave({ id: todoId, note: next })
    },
    [todoId, debouncedSave]
  )

  const open = Boolean(todoId)

  if (!open) return null

  return (
    <div
      role="presentation"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        pointerEvents: 'auto',
      }}
    >
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close note drawer"
        onClick={handleClose}
        style={{
          position: 'absolute',
          inset: 0,
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'default',
          background: 'rgba(0, 0, 0, 0.06)',
        }}
      />

      {/* Panel */}
      <aside
        aria-label="Todo note"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? '100%' : 'min(420px, 100%)',
          background: 'var(--color-paper)',
          borderLeft: '1px solid var(--color-border)',
          boxShadow: '-6px 0 28px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'toduxDrawerIn 0.18s ease-out',
        }}
      >
        <style>{`
          @keyframes toduxDrawerIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>

        <header
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '14px 14px 12px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <span
              className="display-serif"
              style={{
                fontSize: '1rem',
                lineHeight: 1.45,
                color: 'var(--color-ink)',
                display: 'block',
                wordBreak: 'break-word',
              }}
            >
              {todo?.text ?? '…'}
            </span>
            {!todo && (
              <span
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--color-ink-muted)',
                  fontFamily: 'var(--font-serif)',
                  fontStyle: 'italic',
                }}
              >
                Loading todo…
              </span>
            )}
          </div>
          <button
            type="button"
            title="Close"
            onClick={handleClose}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              flexShrink: 0,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-ink-muted)',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </header>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: '12px 14px 14px',
          }}
        >
          <MarkdownEditor
            key={todoId}
            value={draft}
            onChange={handleDraftChange}
            placeholder="Write a note (markdown)…"
            readOnly={!todo}
            autoFocus={Boolean(todo)}
            minHeight="120px"
            style={{ flex: 1, minHeight: 0 }}
          />
        </div>

        <footer
          style={{
            flexShrink: 0,
            padding: '8px 14px 12px',
            fontSize: '0.75rem',
            color: 'var(--color-ink-faint)',
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          {updateTodo.isPending ? 'Saving…' : '\u00a0'}
        </footer>
      </aside>
    </div>
  )
}
